/**
 * /Recon portal — cross-engagement deliverables view.
 *
 * Aggregates every report across every engagement on one page so the
 * client doesn't need to drill into each engagement separately to find
 * "which Snapshot was for the Q1 assessment again?".
 *
 * Data source: GET /api/v1/client-portal/engagements (each row already
 * includes its reports). MVP just iterates client-side. Bucket Q6+
 * may add a dedicated /api/v1/client-portal/reports endpoint with
 * filter+sort built into the query if list size demands it.
 *
 * Bucket Q1 (cryptographic signing + actual download endpoint) is what
 * makes the row-level "Download" button real. Until Q1 ships, this
 * page surfaces report metadata + the engagement they belong to and
 * links into engagement-detail where the existing "Available soon"
 * placeholder stays in place.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { listEngagementsApiV1ClientPortalEngagementsGet } from "@hackereyes/api-client";
import "./documents.css";

interface Report {
  id: string;
  title: string;
  report_type: string;
  status: string;
  generated_at: string | null;
  published_at: string | null;
  created_at: string;
}

interface Engagement {
  id: string;
  case_number: string;
  title: string;
  reports?: Report[];
}

interface DocumentRow {
  report: Report;
  engagement: { id: string; case_number: string; title: string };
}

const REPORT_STATUS_COPY: Record<string, { label: string; tone: string }> = {
  draft: { label: "Draft", tone: "slate" },
  generating: { label: "Generating", tone: "amber" },
  review: { label: "In review", tone: "cobalt" },
  approved: { label: "Approved", tone: "cyan" },
  published: { label: "Published", tone: "green" },
};

export function PortalDocuments() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["client-engagements-with-reports"],
    queryFn: async () => {
      const { data, error } = await listEngagementsApiV1ClientPortalEngagementsGet();
      if (error) {
        throw new Error(((error as Record<string, unknown>)?.detail as string) || "load_failed");
      }
      return (data ?? []) as unknown as Engagement[];
    },
  });

  // Flatten engagements → rows, sorted newest first by published_at (or
  // generated_at, or created_at as fallback). The same ordering used in
  // every "list of reports" surface across the portal.
  const rows: DocumentRow[] = useMemo(() => {
    if (!data) return [];
    const flat: DocumentRow[] = [];
    for (const eng of data) {
      // The /engagements list endpoint may not include reports in its
      // lightweight summary shape. If reports are missing, skip — the
      // client can drill into the engagement detail to see them. When
      // we add the full /reports cross-engagement endpoint in a follow-
      // up, this fallback goes away.
      for (const r of eng.reports ?? []) {
        flat.push({
          report: r,
          engagement: { id: eng.id, case_number: eng.case_number, title: eng.title },
        });
      }
    }
    flat.sort((a, b) => {
      const ad = a.report.published_at || a.report.generated_at || a.report.created_at;
      const bd = b.report.published_at || b.report.generated_at || b.report.created_at;
      return new Date(bd).getTime() - new Date(ad).getTime();
    });
    return flat;
  }, [data]);

  const engagementsWithoutReports = useMemo(() => {
    if (!data) return 0;
    return data.filter((e) => !e.reports || e.reports.length === 0).length;
  }, [data]);

  return (
    <div className="documents-page">
      <div className="documents-inner">
        <div className="documents-header">
          <div className="eyebrow">Your documents</div>
          <h1 className="documents-title">Documents</h1>
          <p className="documents-lede">
            Every deliverable across every engagement, newest first. Open
            any document's engagement to see scope context and the rest of
            the deliverable bundle.
          </p>
        </div>

        {isLoading && (
          <div className="documents-loading">Loading documents…</div>
        )}

        {error && (
          <div className="documents-error">
            We couldn't load your documents. Try refreshing, or contact
            your assessor if the problem persists.
          </div>
        )}

        {data && rows.length === 0 && (
          <div className="documents-empty">
            <h2 className="documents-empty-title">No documents yet.</h2>
            <p className="documents-empty-body">
              Deliverables appear here when your engagements produce them.
              {engagementsWithoutReports > 0 && (
                <>
                  {" "}You have {engagementsWithoutReports} engagement
                  {engagementsWithoutReports === 1 ? "" : "s"} in flight —
                  documents will land as the assessment runs.
                </>
              )}
            </p>
          </div>
        )}

        {rows.length > 0 && (
          <div className="documents-list">
            {rows.map(({ report, engagement }) => {
              const statusCopy = REPORT_STATUS_COPY[report.status] || {
                label: report.status,
                tone: "slate",
              };
              const stamp = report.published_at || report.generated_at || report.created_at;
              const date = new Date(stamp).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              });
              return (
                <Link
                  key={report.id}
                  to={`/portal/engagements/${engagement.id}`}
                  className="documents-row"
                >
                  <div className="documents-row-left">
                    <div className="documents-row-title">{report.title}</div>
                    <div className="documents-row-meta">
                      {report.report_type.replace(/_/g, " ")} · {engagement.case_number}{" "}
                      · {date}
                    </div>
                  </div>
                  <div className="documents-row-right">
                    <span className={`documents-status documents-status-${statusCopy.tone}`}>
                      {statusCopy.label}
                    </span>
                    <span className="documents-arrow" aria-hidden="true">→</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
